/**
 * Thin HTTP client for the Bridge API.
 *
 * Every user operation is an "action": POST /v1/actions/{id} with a JSON body.
 * Errors are RFC 9457 problem+json documents which we turn into ApiError.
 */

export interface Problem {
  type?: string
  title?: string
  status: number
  code: string
  detail?: string
  errors?: Record<string, string>
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string
  readonly fields: Record<string, string>

  constructor(p: Problem) {
    super(p.detail || p.title || p.code || `HTTP ${p.status}`)
    this.name = 'ApiError'
    this.status = p.status
    this.code = p.code || 'unknown'
    this.detail = p.detail ?? ''
    this.fields = p.errors ?? {}
  }

  /** The action is not registered on this server (module not deployed yet). */
  get isNotFoundAction(): boolean {
    return this.status === 404 && this.code === 'action.not_found'
  }
  get isUnauthorized(): boolean {
    return this.status === 401
  }
  get isForbidden(): boolean {
    return this.status === 403
  }
  get isValidation(): boolean {
    return this.status === 422
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

type UnauthorizedHandler = (err: ApiError) => void
let onUnauthorized: UnauthorizedHandler | null = null

/** Register a global handler for 401 responses (redirect to login). */
export function setUnauthorizedHandler(h: UnauthorizedHandler | null) {
  onUnauthorized = h
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Partial<Problem> = {}
  try {
    const text = await res.text()
    body = text ? (JSON.parse(text) as Partial<Problem>) : {}
  } catch {
    body = {}
  }
  return new ApiError({
    status: res.status,
    code: body.code ?? (res.status === 0 ? 'network' : `http_${res.status}`),
    detail: body.detail ?? body.title ?? res.statusText,
    title: body.title,
    errors: body.errors,
  })
}

export interface RequestOptions {
  signal?: AbortSignal
  /** Do not trigger the global 401 handler (used by the login page and the auth probe). */
  silent401?: boolean
}

export async function request<T>(
  method: 'GET' | 'POST',
  url: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  let res: Response
  try {
    res = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        // CSRF defence for cookie-authenticated requests: a custom header cannot be sent cross-site without CORS preflight.
        'X-Requested-With': 'bridge',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: opts.signal,
    })
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e
    throw new ApiError({ status: 0, code: 'network', detail: 'Network error' })
  }
  if (!res.ok) {
    const err = await parseError(res)
    if (err.isUnauthorized && !opts.silent401) onUnauthorized?.(err)
    throw err
  }
  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/** Invoke an action by id. */
export function callAction<TOut = unknown, TIn = unknown>(id: string, input?: TIn, opts?: RequestOptions): Promise<TOut> {
  return request<TOut>('POST', `/v1/actions/${encodeURIComponent(id)}`, input ?? {}, opts)
}
